import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  layoutFindingLead,
  ptToPx,
  type TextMeasurer,
  type WordFindingLeadInput,
  type WordGeometry,
} from './wordLineBreaker';

interface WordFindingLeadBlockProps {
  lead: WordFindingLeadInput;
  measurer: TextMeasurer | null;
  geometry: WordGeometry;
}

const fmtPt = (pt: number) => `${pt.toFixed(1).replace('.', ',')} pt`;

/**
 * Platzhalter für den Vuln-Header, den Word vor die Detailbeschreibung setzt
 * (Titel, Referenz-ID, Metadaten-Tabelle, Allgemeine Beschreibung, Label).
 * Genau so hoch wie in Word — damit hat die erste Seite sichtbar weniger Platz.
 */
const WordFindingLeadBlock: React.FC<WordFindingLeadBlockProps> = ({ lead, measurer, geometry }) => {
  const layout = useMemo(() => (measurer ? layoutFindingLead(lead, measurer, geometry) : null), [lead, measurer, geometry]);
  const heightPt = layout?.heightPt ?? 0;
  const heightPx = ptToPx(heightPt);
  const parts = layout?.parts ?? [];
  const summary = parts
    .filter((p) => p.label !== 'Leerzeile' && !p.label.endsWith(':'))
    .map((p) => (p.lines > 1 || p.label === 'Allgemeine Beschreibung' || p.label === 'Titel' ? `${p.label} ${p.lines} ${p.lines === 1 ? 'Zeile' : 'Zeilen'}` : p.label))
    .join(' · ');

  return (
    <Box
      data-testid="word-finding-lead"
      data-word-block="vuln-header"
      data-word-height-pt={heightPt.toFixed(2)}
      data-word-lead-parts={JSON.stringify(parts.map((p) => [p.label, p.lines, Math.round(p.heightPt * 10) / 10]))}
      aria-hidden
      sx={{
        height: heightPx,
        boxSizing: 'border-box',
        border: '1px dashed rgba(180, 196, 214, 0.55)',
        borderRadius: 1,
        bgcolor: 'rgba(180, 196, 214, 0.06)',
        color: 'rgba(180, 196, 214, 0.9)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        px: 2,
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        Vuln-Header
      </Typography>
      <Typography sx={{ fontSize: '0.72rem', lineHeight: 1.4, mt: 0.5 }}>
        Word setzt hier Titel, Referenz-ID, Metadaten-Tabelle und Allgemeine Beschreibung — die erste Seite hat
        entsprechend weniger Platz.
      </Typography>
      {layout && (
        <Typography data-testid="word-finding-lead-summary" sx={{ fontSize: '0.68rem', lineHeight: 1.4, mt: 0.75, opacity: 0.85 }}>
          {summary} · {fmtPt(heightPt)}
        </Typography>
      )}
    </Box>
  );
};

export default WordFindingLeadBlock;
